import { differenceInDays, differenceInHours, parseISO, isValid } from "date-fns";
import { Clock, AlertTriangle, CalendarX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface DeadlineBadgeProps {
  deadline: string | null | undefined;
  size?: "sm" | "md";
  className?: string;
}

function getUrgency(deadline: Date) {
  const now = new Date();
  const days = differenceInDays(deadline, now);
  const hours = differenceInHours(deadline, now);

  if (hours < 0) {
    return { label: "Expirada", level: "expired" as const, icon: CalendarX };
  }
  if (days === 0) {
    return { label: "Último dia!", level: "critical" as const, icon: AlertTriangle };
  }
  if (days <= 3) {
    return { label: `${days}d restantes`, level: "warning" as const, icon: AlertTriangle };
  }
  if (days <= 7) {
    return { label: `${days} dias`, level: "soon" as const, icon: Clock };
  }
  return { label: `${days} dias`, level: "normal" as const, icon: Clock };
}

const URGENCY_STYLES: Record<string, string> = {
  expired: "bg-destructive/10 text-destructive border-destructive/20",
  critical: "bg-destructive/10 text-destructive border-destructive/20",
  warning: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  soon: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
  normal: "bg-muted text-muted-foreground border-border",
};

export function DeadlineBadge({ deadline, size = "md", className }: DeadlineBadgeProps) {
  if (!deadline) return null;

  const date = parseISO(deadline);
  if (!isValid(date)) return null;

  const { label, level, icon: Icon } = getUrgency(date);

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-medium border",
        URGENCY_STYLES[level],
        size === "sm" && "text-[10px] px-1.5 py-0.5",
        size === "md" && "text-xs px-2 py-0.5",
        className,
      )}
    >
      <Icon className={cn(size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5")} />
      {label}
    </Badge>
  );
}

export { getUrgency };
